/**
 * ONE canonical credential redactor (compliance-pii law — never a second
 * implementation). Strips live secrets out of routes and free-text error
 * content before either reaches a bug-report persist sink.
 *
 * Token path routes (public, unauthenticated — the path segment itself IS
 * the credential): /contract/view/:token, /issue-status/:token,
 * /staff-disc/:token, /tour/manage/:token (see `src/routes/registry.tsx`).
 *
 * Query params that carry live secrets: `token` (/register-child),
 * `code` + `state` (/gmail-callback OAuth), `t` (/docs share token).
 */
const REDACTED = '[redacted]';

const TOKEN_PATH_PREFIXES = 'contract\\/view|issue-status|staff-disc|tour\\/manage';

/** Anchored — matches only at the start of a `pathname`. */
const TOKEN_PATH = new RegExp(`^\\/(${TOKEN_PATH_PREFIXES})\\/[^/?#]+`);

/** Unanchored, global — matches a token path segment anywhere in free text
 *  (e.g. a chunk-load error message embedding the failing module URL). */
const TOKEN_PATH_EMBEDDED = new RegExp(`\\/(${TOKEN_PATH_PREFIXES})\\/[^/?#\\s'")]+`, 'g');

const SECRET_PARAMS = ['token', 'code', 'state', 't'];
const SECRET_PARAM_PATTERN = new RegExp(`([?&])(${SECRET_PARAMS.join('|')})=([^&]*)`, 'g');

function redactPath(path: string): string {
  return path.replace(TOKEN_PATH, (_match, prefix: string) => `/${prefix}/${REDACTED}`);
}

function redactQuery(search: string): string {
  if (!search) return search;
  return search.replace(SECRET_PARAM_PATTERN, (_match, sep: string, key: string) => `${sep}${key}=${REDACTED}`);
}

/** Redacts a `pathname + search` string (e.g. `window.location.pathname +
 *  window.location.search`) — token path segments and secret query-param
 *  values become `[redacted]`; everything else passes through byte-identical. */
export function redactRoute(pathAndSearch: string): string {
  const qIndex = pathAndSearch.indexOf('?');
  const path = qIndex === -1 ? pathAndSearch : pathAndSearch.slice(0, qIndex);
  const search = qIndex === -1 ? '' : pathAndSearch.slice(qIndex);
  return redactPath(path) + redactQuery(search);
}

/** Redacts token-path occurrences and secret query-param values anywhere
 *  inside free text (error messages, stack traces, user-typed notes) — e.g. a
 *  chunk-load failure message that embeds the full failing URL, or a staff
 *  member pasting a live link into a bug-report note. */
export function redactEmbedded(text: string): string {
  return redactQuery(text.replace(TOKEN_PATH_EMBEDDED, (_match, prefix: string) => `/${prefix}/${REDACTED}`));
}
